import { useEffect, useRef, useCallback } from 'react';
import { sendSessionHeartbeat, logActivity } from '@/lib/api';
import { v4 as uuidv4 } from 'uuid';

const HEARTBEAT_INTERVAL = 30000; // 30 seconds
const IDLE_THRESHOLD = 60000; // 1 minute of no activity = idle

export function useActivityTracker(userId, isEnabled = true) {
  const sessionIdRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const activeSecondsRef = useRef(0);
  const idleSecondsRef = useRef(0);
  const heartbeatIntervalRef = useRef(null);
  const isActiveRef = useRef(true);

  // Initialize session ID
  useEffect(() => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = uuidv4();
    }
  }, []);

  // Track user activity (mouse, keyboard, scroll, touch)
  const handleActivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    // If was idle and now active, log it
    if (!isActiveRef.current && timeSinceLastActivity < IDLE_THRESHOLD) {
      isActiveRef.current = true;
    }
    
    lastActivityRef.current = now;
  }, []);

  // Track screenshot attempts (limited browser support)
  const handleKeyDown = useCallback((e) => {
    // Detect common screenshot key combinations
    const isScreenshotAttempt = 
      (e.key === 'PrintScreen') ||
      (e.metaKey && e.shiftKey && (e.key === '3' || e.key === '4' || e.key === '5')) || // Mac
      (e.ctrlKey && e.key === 'PrintScreen') || // Windows
      (e.altKey && e.key === 'PrintScreen'); // Windows active window
    
    if (isScreenshotAttempt && userId) {
      logActivity('screenshot_attempt', userId, {
        key_combination: `${e.ctrlKey ? 'Ctrl+' : ''}${e.altKey ? 'Alt+' : ''}${e.metaKey ? 'Cmd+' : ''}${e.shiftKey ? 'Shift+' : ''}${e.key}`
      }).catch(() => {});
    }
    
    handleActivity();
  }, [userId, handleActivity]);

  // Send heartbeat
  const sendHeartbeat = useCallback(async () => {
    if (!userId || !sessionIdRef.current || !isEnabled) return;

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    const wasActive = timeSinceLastActivity < IDLE_THRESHOLD;
    
    // Calculate seconds since last heartbeat
    const intervalSeconds = HEARTBEAT_INTERVAL / 1000;
    
    if (wasActive) {
      activeSecondsRef.current += intervalSeconds;
      isActiveRef.current = true;
    } else {
      idleSecondsRef.current += intervalSeconds;
      isActiveRef.current = false;
    }

    try {
      await sendSessionHeartbeat({
        user_id: userId,
        session_id: sessionIdRef.current,
        is_active: wasActive,
        active_seconds: wasActive ? intervalSeconds : 0,
        idle_seconds: wasActive ? 0 : intervalSeconds
      });
    } catch (error) {
      console.error('Failed to send heartbeat:', error);
    }
  }, [userId, isEnabled]);

  // Set up event listeners and heartbeat
  useEffect(() => {
    if (!isEnabled || !userId) return;

    // Activity event listeners
    const events = ['mousemove', 'mousedown', 'scroll', 'touchstart', 'touchmove'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    // Keyboard events (also for screenshot detection)
    window.addEventListener('keydown', handleKeyDown);
    
    // Visibility change (track when tab is hidden/shown)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Start heartbeat interval
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
    
    // Send initial heartbeat
    sendHeartbeat();
    
    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [userId, isEnabled, handleActivity, handleKeyDown, sendHeartbeat]);

  return {
    sessionId: sessionIdRef.current,
    isActive: isActiveRef.current,
    activeSeconds: activeSecondsRef.current,
    idleSeconds: idleSecondsRef.current
  };
}

export default useActivityTracker;
